<template>
  <div>
    <InvoiceListBase
      ref="invoiceListBase"
      :title="$t('MENU.ITEM.EXP.AI_INVOICES')"
      :showTypeFilter="true"
      :invoiceTypeOptions="invoiceTypeOptions"
      :additionalHeaders="typeHeader"
      :getCurrentInvoiceCount="getCurrentInvoiceCount"
      :accountingValueTypesMap="filteredAccountingValueTypesMap"
      :loadingAccountingValues="loadingAccountingValues"
      @item-expanded="handleItemExpanded"
    >
      <template v-slot:type-column="{ item, isEditingMain, markMainAsChanged }">
        <td>
          <v-autocomplete
            v-if="isEditingMain(item.id)"
            v-model="item.aiInvoiceTypeId"
            :items="invoiceTypeOptions"
            item-text="name"
            item-value="id"
            dense
            hide-details
            @change="markMainAsChanged(item)"
          ></v-autocomplete>
          <span v-else>{{ item.type ? item.type.name : "-" }}</span>
        </td>
      </template>

      <template v-slot:extra-actions="{ item }">
        <v-btn
          v-if="!item.isProcessed"
          icon
          small
          color="error"
          class="ml-1"
          @click="showDeleteModal(item)"
        >
          <v-icon small>mdi-delete</v-icon>
        </v-btn>
      </template>
    </InvoiceListBase>

    <b-modal
      ref="deleteModal"
      :title="$t('LABELS.DELETE_INVOICE')"
      @ok="confirmDelete"
      ok-variant="danger"
      :ok-title="$t('BUTTON.DELETE')"
      :cancel-title="$t('LABELS.CANCEL')"
    >
      <p>{{ $t("LABELS.CONFIRM_DELETE") }}</p>
    </b-modal>
  </div>
</template>

<script>
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import InvoiceListBase from "../components/InvoiceListBase.vue";
import {
  FETCH_AI_INVOICE_TYPES,
  COUNT_AI_INVOICES,
  DELETE_AI_INVOICE
} from "@/modules/ai-invoices/store/ai-invoices.module";
import { FETCH_AI_INVOICE_ACCOUNTING_VALUE_TYPES } from "@/modules/ai-Invoice-accounting-value-types/store/ai-invoice-accounting-value-types.module";

export default {
  name: "AiInvoicesList",
  components: {
    InvoiceListBase
  },
  data() {
    return {
      invoiceTypeOptions: [],
      typeHeader: [
        {
          text: this.$t("FORM_LABELS.TYPE"),
          value: "type",
          sortable: false
        }
      ],
      itemToDelete: null,
      allAccountingValueTypes: [],
      expandedInvoices: [],
      loadingAccountingValues: false
    };
  },
  computed: {
    filteredAccountingValueTypesMap() {
      // Map invoiceId -> filtered accountingValueTypes
      // This is so that we can show only relevant accounting value types for each invoice when expanded
      // and not have to fetch them separately for each invoice. It works as a "cache"
      // of filtered types based on the invoice type's allowed accounting value types to display in the edit form.
      const allTypes = Array.isArray(this.allAccountingValueTypes)
        ? this.allAccountingValueTypes
        : [];
      const map = {};
      this.expandedInvoices.forEach(item => {
        if (!item || !item.type) {
          map[item.id] = allTypes;
          return;
        }
        const invoiceType = item.type;
        if (
          invoiceType.accountingValueTypes &&
          Array.isArray(invoiceType.accountingValueTypes) &&
          invoiceType.accountingValueTypes.length > 0
        ) {
          const allowedTypeIds = invoiceType.accountingValueTypes.map(
            avt => avt.id
          );
          map[item.id] = allTypes.filter(avt =>
            allowedTypeIds.includes(avt.id)
          );
        } else {
          map[item.id] = allTypes;
        }
      });
      return map;
    }
  },
  async mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      {
        title: this.$i18n.t("MENU.ITEM.EXP.AI_INVOICES"),
        route: { name: "list-ai-invoices" }
      },
      { title: this.$i18n.t("MENU.ITEM.ASSETS.LIST") }
    ]);
    await this.fetchInvoiceTypes();
    await this.fetchAllAccountingValueTypes();
  },
  methods: {
    async fetchInvoiceTypes() {
      try {
        const data = await this.$store.dispatch(FETCH_AI_INVOICE_TYPES);
        this.invoiceTypeOptions = data.data;
      } catch (error) {
        console.error("Error fetching invoice types:", error);
        this.invoiceTypeOptions = [];
      }
    },
    async fetchAllAccountingValueTypes() {
      this.loadingAccountingValues = true;
      try {
        const data = await this.$store.dispatch(
          FETCH_AI_INVOICE_ACCOUNTING_VALUE_TYPES
        );
        this.allAccountingValueTypes = Array.isArray(data.data.items)
          ? data.data.items
          : [];
      } catch (error) {
        console.error("Error fetching accounting value types:", error);
        this.allAccountingValueTypes = [];
        this.$notify({
          group: "notify",
          type: "error",
          text: this.$t("LABELS.ERROR_LOADING_ACCOUNTING_VALUES")
        });
      } finally {
        this.loadingAccountingValues = false;
      }
    },
    handleItemExpanded({ item, value }) {
      if (value) {
        if (!this.expandedInvoices.some(inv => inv.id === item.id)) {
          this.expandedInvoices.push(item);
        }
      } else {
        this.expandedInvoices = this.expandedInvoices.filter(
          inv => inv.id !== item.id
        );
      }
    },
    async getCurrentInvoiceCount() {
      try {
        const data = await this.$store.dispatch(COUNT_AI_INVOICES);
        return data.count || 0;
      } catch (e) {
        return 0;
      }
    },
    showDeleteModal(item) {
      this.itemToDelete = item;
      this.$refs.deleteModal.show();
    },
    async confirmDelete() {
      if (!this.itemToDelete) return;

      try {
        await this.$store.dispatch(DELETE_AI_INVOICE, this.itemToDelete.id);
        this.$notify({
          group: "notify",
          type: "success",
          text: this.$t("ALERTS.SUCCESS")
        });
        this.itemToDelete = null;
        this.$refs.invoiceListBase.getDataFromApi();
      } catch (error) {
        this.$notify({
          group: "notify",
          type: "error",
          text: this.$t("ALERTS.ERROR")
        });
      }
    }
  }
};
</script>
